import { applyViewportOffsetClamp } from '@/infrastructure/engine2d/helpers/applyViewportOffsetClamp';
import { applyPanTransformation } from '@/infrastructure/engine2d/input/panCallback';
import { Point, type Canvas, type TPointerEvent } from 'fabric';
import type { ITouchZoomState } from '@/infrastructure/engine2d/types/ITouchZoomState';
import type { TNotifyZoom } from '@/infrastructure/engine2d/input/zoomCallback';
import type { TViewportConstraints } from '@/utils/TViewportConstraints';

// Two-finger touch state machine used by panCallback to interleave
// pinch-zoom + pan on the same gesture. Kept out of zoomCallback
// because it wants panCallback's applyPanTransformation and
// panCallback wants this state to fire on two-finger touches — the
// pair lives in input/ side by side.

function createTouchZoomState(): ITouchZoomState {
    return {
        lastDistance: 0,
        lastZoom: 1,
        lastTouchCenter: new Point(0, 0),
        isZooming: false,
        zoomTimeout: null,
    };
}

function handleTwoFingerTouch(
    e: TouchEvent,
    state: ITouchZoomState,
    fabricCanvas: Canvas,
    constraints: TViewportConstraints,
    notifyZoom: TNotifyZoom,
): boolean {
    if (e.touches.length !== 2) {
        return false;
    }

    e.preventDefault();
    e.stopPropagation();

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    const distance = _calculateTouchDistance(touch1, touch2);
    const canvasRect = fabricCanvas.getElement().getBoundingClientRect();
    const touchCenter = _calculateTouchCenter(touch1, touch2, canvasRect);

    if (e.type === 'touchstart' || state.lastDistance === 0) {
        _initializePinchZoom(state, distance, touchCenter, fabricCanvas);
        return true;
    }

    if (e.type === 'touchmove' && state.lastDistance > 0 && state.isZooming) {
        _setZoomResetTimeout(state);

        // Handle zoom
        const zoomScaleFactor = distance / state.lastDistance;
        const newZoom = state.lastZoom * zoomScaleFactor;
        _applyConstrainedZoom(fabricCanvas, touchCenter, newZoom, constraints, notifyZoom);

        // Handle pan (movement of the center point between fingers)
        const panDelta = new Point(touchCenter.x - state.lastTouchCenter.x, touchCenter.y - state.lastTouchCenter.y);
        applyPanTransformation(fabricCanvas, panDelta);

        // Update state for next iteration
        _updateTouchZoomState(state, distance, touchCenter, fabricCanvas);
        fabricCanvas.renderAll();
    }

    return true;
}

function handleTouchEnd(e: TouchEvent, state: ITouchZoomState): boolean {
    if (e.type === 'touchend' || e.type === 'touchcancel') {
        if (e.touches.length < 2) {
            _resetTouchZoomState(state);
        }
        return true;
    }
    return false;
}

// Safety check: if we're marked as zooming but don't have a TouchEvent
// with 2 fingers anymore, reset.
function validateZoomState(e: TPointerEvent, state: ITouchZoomState): void {
    if (window.TouchEvent && state.isZooming && (!(e instanceof window.TouchEvent) || e.touches.length !== 2)) {
        _resetTouchZoomState(state);
    }
    if (window.TouchEvent && e instanceof window.TouchEvent && e.touches.length !== 2 && state.isZooming) {
        _resetTouchZoomState(state);
    }
}

function _initializePinchZoom(state: ITouchZoomState, distance: number, touchCenter: Point, fabricCanvas: Canvas): void {
    state.lastDistance = distance;
    state.lastZoom = fabricCanvas.getZoom();
    state.lastTouchCenter = touchCenter;
    state.isZooming = true;
    _setZoomResetTimeout(state);
}

function _applyConstrainedZoom(
    fabricCanvas: Canvas,
    touchCenter: Point,
    newZoom: number,
    constraints: TViewportConstraints,
    notifyZoom: TNotifyZoom,
): void {
    const { max, min } = constraints.zoomBounds;
    fabricCanvas.zoomToPoint(touchCenter, Math.min(Math.max(newZoom, min), max));
    applyViewportOffsetClamp(fabricCanvas, constraints);
    notifyZoom(fabricCanvas.getZoom());
}

function _resetTouchZoomState(state: ITouchZoomState): void {
    state.lastDistance = 0;
    state.isZooming = false;
    if (state.zoomTimeout) {
        clearTimeout(state.zoomTimeout);
        state.zoomTimeout = null;
    }
}

function _updateTouchZoomState(state: ITouchZoomState, distance: number, touchCenter: Point, fabricCanvas: Canvas): void {
    state.lastDistance = distance;
    state.lastZoom = fabricCanvas.getZoom();
    state.lastTouchCenter = touchCenter;
}

// Reset zoom state after 500ms of inactivity to avoid stale locks
// when a touchend event is missed.
function _setZoomResetTimeout(state: ITouchZoomState): void {
    if (state.zoomTimeout) {
        clearTimeout(state.zoomTimeout);
    }
    state.zoomTimeout = setTimeout(() => _resetTouchZoomState(state), 500);
}

function _calculateTouchDistance(touch1: Touch, touch2: Touch): number {
    return Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
}

function _calculateTouchCenter(touch1: Touch, touch2: Touch, canvasRect: DOMRect): Point {
    const centerX = (touch1.clientX + touch2.clientX) / 2 - canvasRect.left;
    const centerY = (touch1.clientY + touch2.clientY) / 2 - canvasRect.top;
    return new Point(centerX, centerY);
}

export { createTouchZoomState, handleTouchEnd, handleTwoFingerTouch, validateZoomState };
